import { Input } from "@arcadeai/design-system/components/ui/atoms/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@arcadeai/design-system/rhf";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { fallback } from "@tanstack/zod-adapter";
import { Search } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { debounce } from "@/lib/debounce";
import type { FileRoutesByTo } from "@/routeTree.gen";

export const nameFilterSchema = z.object({
  name: fallback(z.string(), "").default(""),
});

type NameFilterProps = {
  to: keyof FileRoutesByTo | ".";
  label?: string;
};

// Constants for filtering
const NAME_FILTER_DEBOUNCE_DELAY = 300;

export const NameFilter = ({ to, label = "Name filter" }: NameFilterProps) => {
  const navigate = useNavigate();
  const search = useSearch({
    from: "/_authenticated/orgs/$organizationId/projects/$projectId/auth/oauth/",
  });

  const form = useForm<{ name?: string }>({
    resolver: zodResolver(nameFilterSchema),
    defaultValues: {
      name: search.name ?? "",
    },
    mode: "onChange",
  });

  const debouncedNavigate = debounce((value: string) => {
    navigate({
      to,
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        name: value,
      }),
    });
  }, NAME_FILTER_DEBOUNCE_DELAY);

  const onSubmit = (values: { name?: string }) => {
    const name = values.name?.trim() || "";
    debouncedNavigate(name);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <div className="relative">
                  <Search className="absolute top-2 left-2 size-4 text-muted-foreground" />
                  <Input
                    aria-label={label}
                    className="h-8 w-48 rounded-lg pl-8 lg:w-80"
                    placeholder="Search by name"
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      form.handleSubmit(onSubmit)();
                    }}
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
};
